"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var __1 = require("..");
var binaryOperator_1 = require("../ast/binaryOperator");
var parser_1 = require("../parser/parser");
var signatureValidator_1 = __importDefault(require("../staticAnalyzer/signatureValidator"));
var staticAnalyzer_1 = __importDefault(require("../staticAnalyzer/staticAnalyzer"));
var checker_1 = require("../types/checker");
var formatter_1 = require("../types/formatter");
var type_1 = require("../types/type");
var evaluationErrors_1 = __importStar(require("./evaluationErrors"));
var functionEvaluationContext_1 = require("./functionEvaluationContext");
var util_1 = require("./util");
var value_1 = require("./value");
/**
 * The evaluator represents the context in which a formula is evaluated.
 *
 * It stores the current state of the evaluation, as well as an {@link EvaluationContext} that provides the variables
 * and functions that may be used by formulas.
 *
 * While by default formulas have no side-effects, thus allowing a single Evaluator to be reused for multiple entirely
 * separate formulas, doing so is not recommended. Custom function implementations may modify the state of the evaluator
 * or alter what functions/variables are available in the given context causing potentially inconsistent behavior from
 * run to run.
 *
 * This class may be subclassed to extend it with custom behavior, such as custom variable/function lookups or fallbacks
 * for invalid references.
 */
var Evaluator = /** @class */ (function () {
    /**
     * Construct a new evaluator that uses the given {@link EvaluationContext} to lookup functions and variables.
     */
    function Evaluator(context) {
        this.evaluationStates = [];
        this.context = context;
        this.typeDeterminer = new staticAnalyzer_1.default(context);
    }
    Object.defineProperty(Evaluator.prototype, "currentState", {
        get: function () {
            if (!this.evaluationStates.length)
                return null;
            return this.evaluationStates[this.evaluationStates.length - 1];
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Evaluates the given formula and returns the result.
     *
     * This function accepts either a string or AST node for the formula. If the same formula is going to be evaluated
     * repeatedly, it is recommended that it be parsed separately (using {@link parseFormula}) and the AST be provided
     * when evaluating it, to avoid unnecessarily re-parsing it.
     *
     * @param stringOrRootNode The formula to evaluate. Either a string containing a formula which will be parsed using
     *                         {@link parseFormula} or an {@link ASTNode AST node} representing the root node of the formula.
     * @returns The final value produced by the formula expression.
     * @throws {@link ParseError} if an error occurred while parsing the formula.
     * @throws {@link Error} if a string was given that did not contain a formula.
     */
    Evaluator.prototype.evaluate = function (stringOrRootNode) {
        var node;
        if (typeof stringOrRootNode === "string") {
            var result = parser_1.parseFormula(stringOrRootNode);
            if (result) {
                node = result;
            }
            else {
                throw new __1.FormulaError("Could not parse formula from string");
            }
        }
        else {
            node = stringOrRootNode;
        }
        return node.visit(this);
    };
    Evaluator.prototype.visitNumberLiteral = function (node) {
        return value_1.Value.number(node.value);
    };
    Evaluator.prototype.visitStringLiteral = function (node) {
        return value_1.Value.string(node.value);
    };
    Evaluator.prototype.visitArrayLiteral = function (node) {
        var _this = this;
        var elements = [];
        var _loop_1 = function (i) {
            var elemNode = node.elements[i];
            var elemValue = this_1.withState(node, { elementIndex: i }, function () { return elemNode.visit(_this); });
            elements.push(elemValue);
        };
        var this_1 = this;
        for (var i = 0; i < node.elements.length; i++) {
            _loop_1(i);
        }
        return value_1.Value.array(elements);
    };
    Evaluator.prototype.visitBooleanLiteral = function (node) {
        return value_1.Value.boolean(node.value);
    };
    Evaluator.prototype.visitVariableLookup = function (node) {
        var _this = this;
        return this.withState(node, {}, function (state) {
            return _this.context.getVariable(node.name);
        });
    };
    Evaluator.prototype.visitFunctionCall = function (node) {
        var _this = this;
        var context = new functionEvaluationContext_1.FunctionEvaluationContext(this, node);
        var maybeOverloadedDefinition = this.context.lookupFunction(node.name);
        var paramTypes = node.parameters.map(function (param) { return _this.getType(param); });
        var _a = this.resolveOverload(maybeOverloadedDefinition, paramTypes), resolvedDefinition = _a.resolvedDefinition, returnType = _a.returnType;
        var returnValue;
        if (resolvedDefinition.invokeWithASTParameters) {
            returnValue = resolvedDefinition.invoke(node.parameters, context);
        }
        else {
            var parameters = [];
            var _loop_2 = function (i) {
                var paramNode = node.parameters[i];
                var paramValue = this_2.withState(node, { parameterIndex: i }, function () { return _this.evaluate(paramNode); });
                parameters.push(paramValue);
            };
            var this_2 = this;
            for (var i = 0; i < node.parameters.length; i++) {
                _loop_2(i);
            }
            var resolvedParameterTypes = parameters.map(function (param) { return param.type; });
            if (!signatureValidator_1.default.validateParameters(resolvedParameterTypes, resolvedDefinition)) {
                throw new Error('Parameter values for function invocation do not match parameter types of resolved signature');
            }
            returnValue = resolvedDefinition.invoke(parameters, context);
        }
        if (resolvedDefinition.signature && !checker_1.valueMatchesType(returnValue, returnType)) {
            var message = "Invalid return type '" + formatter_1.formatType(returnValue.type) + "' from function '" + node.name + "', expected '" + formatter_1.formatType(returnType) + "'";
            throw new evaluationErrors_1.FunctionSignatureError([{ message: message }]);
        }
        return returnValue;
    };
    Evaluator.prototype.resolveOverload = function (definition, parameters) {
        var _a;
        if (!Array.isArray(definition)) {
            var _b = signatureValidator_1.default.validateParameters(parameters, definition), errors = _b.errors, returns = _b.returns;
            if (errors.length === 0) {
                return { resolvedDefinition: definition, returnType: returns !== null && returns !== void 0 ? returns : type_1.AnyType };
            }
            else {
                throw new evaluationErrors_1.FunctionSignatureError(errors);
            }
        }
        var results = definition.map(function (overload) {
            var _a = signatureValidator_1.default.validateParameters(parameters, overload), errors = _a.errors, returns = _a.returns;
            return {
                resolvedOverload: overload,
                errors: errors,
                returns: returns,
            };
        });
        var validOverloads = results.filter(function (_a) {
            var errors = _a.errors;
            return errors.length === 0;
        });
        if (validOverloads.length === 1) {
            return { resolvedDefinition: validOverloads[0].resolvedOverload, returnType: (_a = validOverloads[0].returns) !== null && _a !== void 0 ? _a : type_1.AnyType };
        }
        else if (validOverloads.length === 0) {
            throw new evaluationErrors_1.default(results, false);
        }
        else {
            throw new evaluationErrors_1.default(validOverloads, true);
        }
    };
    Evaluator.prototype.visitUnaryMinus = function (node) {
        var _this = this;
        var value = this.withState(node, {}, function () { return node.operand.visit(_this); });
        if (!util_1.isNumberValue(value)) {
            try {
                value = value_1.Value.number(__1.coerceNumber(value));
            }
            catch (e) {
                // rethrow error with the correct message
                throw new evaluationErrors_1.TypeMismatchError("Cannot apply unary minus operator to non-numeric value `" + value + "`");
            }
        }
        return value_1.Value.number(-value.value);
    };
    Evaluator.prototype.visitBinaryOperator = function (node) {
        var _this = this;
        var left = this.withState(node, { operand: "left", }, function () { return node.left.visit(_this); });
        var right = this.withState(node, { operand: "right" }, function () { return node.right.visit(_this); });
        if (!util_1.isNumberValue(left)) {
            try {
                left = value_1.Value.number(__1.coerceNumber(left));
            }
            catch (e) {
                // equals/not-equals can be applied to non-numbers, all others throw error
                if (node.operator !== binaryOperator_1.BinaryOperator.equals && node.operator !== binaryOperator_1.BinaryOperator.notEquals) {
                    throw new evaluationErrors_1.TypeMismatchError("Cannot apply " + binaryOperator_1.getBinaryOperatorString(node.operator) + " operator to non-numeric value `" + left + "`");
                }
            }
        }
        if (!util_1.isNumberValue(right)) {
            try {
                right = value_1.Value.number(__1.coerceNumber(right));
            }
            catch (e) {
                if (node.operator !== binaryOperator_1.BinaryOperator.equals && node.operator !== binaryOperator_1.BinaryOperator.notEquals) {
                    throw new evaluationErrors_1.TypeMismatchError("Cannot apply " + binaryOperator_1.getBinaryOperatorString(node.operator) + " operator to non-numeric value `" + right + "`");
                }
            }
        }
        var lhs = util_1.isArrayValue(left) ? left.elements : left.value;
        var rhs = util_1.isArrayValue(right) ? right.elements : right.value;
        var result = binaryOperator_1.evaluateBinaryOperator(lhs, node.operator, rhs);
        if (typeof result === "boolean") {
            return value_1.Value.boolean(result);
        }
        else {
            return value_1.Value.number(result);
        }
    };
    Evaluator.prototype.visitGroupNode = function (node) {
        var _this = this;
        return this.withState(node, {}, function () { return node.content.visit(_this); });
    };
    /**
     * Pushes the new evaluation state onto the stack for the duration of the function call
     * and then pops it, returning the result of the function.
     *
     * This function takes an {@link ASTNode} of some type, as well as an object containing all the properties of  the
     * matching {@link EvaluationState} interface (except `node`) and constructs the entire concrete evaluation state object.
     *
     * @param node The AST node that this state belongs to. Determines which member of the {@link AnyEvaluationState}
     *             union the concrete state type is.
     * @param restState An object containing any fields of the concrete state type, except for the {@link EvaluationState.node}
     *                  field.
     * @param func The function to be called while the new state is the current state. This function may optionally
     *             receive the new current state back as a parameter.
     */
    Evaluator.prototype.withState = function (node, restState, func) {
        // The `as S` in this declaration is less than ideal, but there doesn't seem to be any way of convincing TypeScript
        // that removing 'node' from S by Omitting it and then re-adding it back produces an S, because it thinks that S
        // could be a class an not a plain JS object. But we control AnyEvaluationState and can say for certain that
        // all union members will be JS objects, and just cast back to S.
        var state = __assign({ node: node }, restState);
        this.evaluationStates.push(state);
        var result = func(state);
        this.evaluationStates.pop();
        return result;
    };
    Evaluator.prototype.getType = function (node) {
        return this.typeDeterminer.getType(node);
    };
    return Evaluator;
}());
exports.default = Evaluator;
//# sourceMappingURL=evaluator.js.map